import React, { MouseEvent, useEffect, useState } from "react";

import cx from "classnames";
import Link from "next/link";
import { CgCheck } from "react-icons/cg";

import { ApplicationTimelineProps } from "components/application-timeline/application-timeline";
import { ApplicationStateEnum, getApplicationState } from "utils/application-state";
import { useApplicationStore } from "utils/application-store-context";
import { getStepUrl } from "utils/url";

const IndexDisc: React.FC<React.PropsWithChildren<Readonly<ApplicationTimelineItemProps>>> = ({
  applyStepIterator,
  cohort,
  program,
  inverse,
  index,
  applyStep,
}) => {
  const { findApplication } = useApplicationStore();
  const existingApplication = findApplication(program.slug, cohort.slug);
  const [applicationState, setApplicationState] = useState<ApplicationStateEnum>();

  useEffect(() => {
    if (existingApplication) {
      const _applicationState = getApplicationState(applyStepIterator.form.formDefinition, existingApplication.data);
      setApplicationState(_applicationState);
    }
  }, [existingApplication, applyStepIterator.form.formDefinition]);

  const checkIfDisabled = (): boolean => applyStepIterator.index !== 0 && !existingApplication?.data?.full_name;

  const outerClasses = [
    "m-auto flex justify-center items-center z-20 relative",
    "h-8 w-8",
    "block text-center font-extrabold flex",
    "items-center justify-center rounded-full",
    {
      "bg-white": !inverse,
      "bg-od-blue": inverse,
      "border border-od-blue": applyStepIterator.slug === applyStep.slug && !inverse,
      "border border-white": applyStepIterator.slug === applyStep.slug && inverse,
      "text-od-blue border border-od-blue bg-white": applyStepIterator.slug !== applyStep.slug && !inverse,
      "text-white border border-white bg-white": applyStepIterator.slug !== applyStep.slug && inverse,
      "hover:text-white hover:bg-od-blue": applyStepIterator.slug !== applyStep.slug && !checkIfDisabled() && !inverse,
      "hover:text-od-blue hover:bg-white": applyStepIterator.slug !== applyStep.slug && !checkIfDisabled() && inverse,
      "cursor-not-allowed": applyStepIterator.slug !== applyStep.slug && checkIfDisabled(),
      "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500": !checkIfDisabled(),
    },
  ];

  const innerClass = [
    "h-2.5 w-2.5",
    "items-center justify-center rounded-full",
    {
      "bg-od-blue": applyStepIterator.slug === applyStep.slug && !inverse,
      "bg-white": applyStepIterator.slug === applyStep.slug && inverse,
    },
  ];

  const innerCompletedClass = [
    "h-full w-full",
    "items-center justify-center rounded-full",
    inverse ? "bg-white" : "bg-od-blue",
  ];

  const stopPropagationIfDisabled = (e: MouseEvent<HTMLElement>): void => {
    if (checkIfDisabled()) {
      e.preventDefault();
    }
  };

  return (
    <>
      <Link
        href={getStepUrl(program.slug, cohort.slug, applyStepIterator.slug)}
        passHref
        onClick={stopPropagationIfDisabled}
        className={cx(outerClasses)}
      >
        {applicationState === ApplicationStateEnum.COMPLETED ? (
          <span className={cx(innerCompletedClass)}>
            <CgCheck className={`w-full h-full font-bold ${inverse ? "text-od-blue" : "text-white"}`} />
          </span>
        ) : (
          <span className={cx(innerClass)} />
        )}
      </Link>
      <div className="flex items-center justify-center mt-2">
        <div className={`text-center ${inverse ? "text-white" : "text-od-blue"}`}>
          {index + 1}. {applyStepIterator.shortTitle}
        </div>
      </div>
    </>
  );
};

type ApplicationTimelineItemProps = Omit<ApplicationTimelineProps, "applySteps"> & {
  // any applyStep from the program's apply-config over which the consumer typically .map()s
  applyStepIterator: ApplicationTimelineProps["applySteps"][number];
  index: number;
};
export const ApplicationTimelineItem: React.FC<React.PropsWithChildren<Readonly<ApplicationTimelineItemProps>>> = ({
  applyStepIterator,
  applyStep,
  index,
  inverse,
  program,
  cohort,
}) => (
  <div key={applyStepIterator.slug} className="px-3">
    <IndexDisc
      applyStepIterator={applyStepIterator}
      applyStep={applyStep}
      inverse={inverse}
      index={index}
      program={program}
      cohort={cohort}
    />
  </div>
);
